import ee from '../services/earth-engine';

/**
 * @function isMultiPolygon
 * @description Whether an area of interest is held as several polygons rather than one ring.
 * A drawn area, and a KML with a single polygon and no holes, stay the ring of `[lng, lat]` pairs
 * the app has always kept; a KML with several polygons, or with islands cut out of one, is kept as
 * GeoJSON MultiPolygon coordinates instead - a list of polygons, each its outer ring followed by
 * its holes. One field holding either shape is what lets the sessions saved before stay readable.
 * @param {Array} [coordinates] - The area as the app keeps it.
 * @memberof module:Shared
 * @returns {Boolean}
 */
export const isMultiPolygon = (coordinates) => Array.isArray(coordinates?.[0]?.[0]?.[0]);

/**
 * @function aoiPolygons
 * @description The area as a list of polygons, whichever shape it is held in.
 * @param {Array} [coordinates] - The area as the app keeps it.
 * @memberof module:Shared
 * @returns {Array<Array<Array<Array<Number>>>>} Polygons, each `[outerRing, ...holes]`; empty when
 *  there is no area.
 */
export const aoiPolygons = (coordinates) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return [];
  return isMultiPolygon(coordinates) ? coordinates : [[coordinates]];
};

/**
 * @function aoiPoints
 * @description Every vertex on the outer boundaries of the area, for framing the map on it or
 * measuring its extent. Holes lie inside their outer ring, so they add nothing to either.
 * @param {Array} [coordinates] - The area as the app keeps it.
 * @memberof module:Shared
 * @returns {Array<Array<Number>>} `[lng, lat]` pairs.
 */
export const aoiPoints = (coordinates) =>
  isMultiPolygon(coordinates) ? coordinates.flatMap((polygon) => polygon[0] || []) : coordinates || [];

/**
 * @function aoiPaths
 * @description The area as Google Maps polygon paths: every ring, holes included, so the outline
 * on the map shows the islands as well as the shore. Google Maps leaves a hole unfilled only when
 * it winds the other way from its outer ring, which a KML does not promise, so holes are turned
 * here when they need to be - otherwise a module that fills its area would paint over the islands.
 * @param {Array} [coordinates] - The area as the app keeps it.
 * @memberof module:Shared
 * @returns {Array<Array<{lat: Number, lng: Number}>>}
 */
export const aoiPaths = (coordinates) => {
  const winding = (ring) =>
    Math.sign(ring.reduce((sum, [x1, y1], i) => {
      const [x2, y2] = ring[(i + 1) % ring.length];
      return sum + (x2 - x1) * (y2 + y1);
    }, 0));
  const toPath = (ring) => ring.map(([lng, lat]) => ({ lat, lng }));
  return aoiPolygons(coordinates).flatMap(([outer, ...holes]) => [
    toPath(outer),
    ...holes.map((hole) => toPath(winding(hole) === winding(outer) ? [...hole].reverse() : hole)),
  ]);
};

/**
 * @constant PACKED_VERTEX_LIMIT
 * @description Above how many vertices an area goes to Earth Engine as one JSON string, decoded on
 * the server, instead of as a geometry written out by the client.
 * The client encodes every coordinate of a geometry as a node of its own, on the page's thread, in
 * every request that touches the area: the 28 846 vertices of the Babitonga KML took 2.6 to 3.6 s
 * per request, and a table of twelve thumbnails held the page frozen for five minutes. Sent as one
 * string, the same area encodes in 40 ms and Earth Engine builds the same geometry from it - same
 * area, no symmetric difference, geodesic edges. A drawn area stays below the limit and is sent as
 * before.
 * @memberof module:Shared
 */
const PACKED_VERTEX_LIMIT = 1000;

/**
 * @function aoiGeometry
 * @description The area as an Earth Engine geometry. Every module builds its region here, so a
 * KML with several polygons is analysed whole instead of through its first ring.
 * An area of more than {@link PACKED_VERTEX_LIMIT} vertices is built on the server from its
 * coordinates as JSON, so it costs one string in each request rather than a node per number; such a
 * geometry has no client-side coordinates, which nothing in the app reads back.
 * @param {Array} coordinates - The area as the app keeps it.
 * @memberof module:Shared
 * @returns {ee.Geometry} A Polygon for a ring, a MultiPolygon otherwise.
 */
export const aoiGeometry = (coordinates) => {
  const multi = isMultiPolygon(coordinates);
  const rings = multi ? coordinates.map((polygon) => polygon.map((ring) => [...ring, ring[0]])) : [coordinates];
  const vertices = aoiPolygons(coordinates).reduce(
    (sum, polygon) => sum + polygon.reduce((count, ring) => count + ring.length, 0),
    0,
  );
  const packed = vertices > PACKED_VERTEX_LIMIT ? ee.String(JSON.stringify(rings)).decodeJSON() : rings;
  return multi ? ee.Geometry.MultiPolygon(packed) : ee.Geometry.Polygon(packed);
};

/**
 * @function aoiAreaKm2
 * @description The area's surface in square kilometres, holes taken out.
 * @param {Array} [coordinates] - The area as the app keeps it.
 * @memberof module:Shared
 * @returns {Number}
 */
export const aoiAreaKm2 = (coordinates) => {
  const area = (ring) =>
    window.google.maps.geometry.spherical.computeArea(ring.map(([lng, lat]) => ({ lat, lng })));
  return (
    aoiPolygons(coordinates).reduce(
      (sum, [outer, ...holes]) => sum + area(outer) - holes.reduce((s, hole) => s + area(hole), 0),
      0,
    ) / 1000000
  );
};

/**
 * @function parseCoordinateList
 * @description The positions in one KML `<coordinates>` element, as an open ring. KML separates
 * tuples by any whitespace - spaces, tabs or line breaks - and may add an altitude to each.
 * @param {String} text - The element's text.
 * @memberof module:Shared
 * @returns {Array<Array<Number>>} `[lng, lat]` pairs, without the repeated closing position.
 */
const parseCoordinateList = (text) => {
  const ring = (text || '')
    .trim()
    .split(/\s+/)
    .map((tuple) => tuple.split(',').map(Number))
    .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat))
    .map(([lng, lat]) => [lng, lat]);
  const first = ring[0];
  const last = ring[ring.length - 1];
  return ring.length > 1 && first[0] === last[0] && first[1] === last[1] ? ring.slice(0, -1) : ring;
};

/**
 * @function parseKmlArea
 * @description Reads the area of interest out of a KML, every polygon in it.
 * The app used to take the first `<coordinates>` it found and stop, which on a KML exported from
 * a GIS - one placemark per piece, islands as inner boundaries - kept a sliver and dropped the
 * rest. Here every `<Polygon>` is read with its holes, from any placemark, folder or
 * MultiGeometry; placemarks with no polygon are skipped. A KML holding no polygon at all - a ring
 * or a line drawn as the area - is still read from its first `<coordinates>`, as before, and so is
 * one that is not well-formed XML, which the old reading accepted.
 * @param {String} text - The KML file's content.
 * @memberof module:Shared
 * @returns {Array|null} A ring when the file holds one polygon without holes, MultiPolygon
 *  coordinates otherwise, `null` when it holds no usable area.
 */
export const parseKmlArea = (text) => {
  const firstRing = () => {
    const ring = parseCoordinateList(/<coordinates>([\s\S]*?)<\/coordinates>/.exec(text || '')?.[1]);
    return ring.length ? ring : null;
  };
  const document = new window.DOMParser().parseFromString(text || '', 'application/xml');
  if (document.getElementsByTagName('parsererror').length) return firstRing();
  const ringOf = (boundary) => parseCoordinateList(boundary?.getElementsByTagNameNS('*', 'coordinates')[0]?.textContent);

  const polygons = Array.from(document.getElementsByTagNameNS('*', 'Polygon'))
    .map((polygon) => {
      const outer = ringOf(polygon.getElementsByTagNameNS('*', 'outerBoundaryIs')[0]);
      const holes = Array.from(polygon.getElementsByTagNameNS('*', 'innerBoundaryIs'))
        .map(ringOf)
        .filter((ring) => ring.length >= 3);
      return outer.length >= 3 ? [outer, ...holes] : null;
    })
    .filter(Boolean);

  if (polygons.length === 1 && polygons[0].length === 1) return polygons[0][0];
  return polygons.length ? polygons : firstRing();
};
